import type { Distance, Margins, SpatialRectangle } from "@pdfking/math"
import { AddMargins, Min, Round, Scale, Subtract, ToMargins, Total } from "@pdfking/math"
import type { ColorID, PageShape, PlotShape } from "@pdfking/renderer-api"
import { ClipShapes, MatrixIdentity, PlotFunction, ShapeBounds } from "@pdfking/renderer-api"
import { Range, Constant } from "@pdfking/tfunc"

import { PALETTE } from "#constants"
import type { RandomStream } from "#internal"

import { RandomPick } from "../random/RandomPick"
import { RoundedRect } from "./RoundedRect"

// A cell on the page's short side, the gap between tiles and their corners
// in cells
const CELL = 0.06
const GAP = 0.45
const RADIUS = 0.35
// The least of a cell a tile cut back for what is over the grid keeps
const SMALLEST = 0.4

// A tile's cells across and up, before it is cut to the room there is:
// dots, pills either way, and blocks
const SIZES: [number, number][] = [
  [1, 1],
  [1, 1],
  [1, 1],
  [2, 1],
  [3, 1],
  [4, 1],
  [5, 1],
  [1, 2],
  [1, 3],
  [1, 4],
  [2, 2],
  [3, 2],
  [2, 3],
  [3, 3],
  [4, 3],
  [3, 4],
]

/** `box` on `ground`, paper unless told, packed with rounded tiles as a
 * backpack grid has them:
 * a grid of cells, `short` fifteen-odd across, each free cell from the
 * corner on starting a tile of a size drawn from dots, pills and blocks, as
 * much of it as fits; each tile inset by the gap, in one of `inks`, the
 * calendar's colors unless told, the gray ones, in the lines, again in the
 * spot: the pantone's and the UV's plate those tiles, the ground none of it. The grid fills the box exactly, its
 * cells stretched to a whole number of them each way, so every edge of it
 * is half a gap of ground: boxes side by side run on into each other, gap
 * for gap. The bleed is the ground's alone. What is set `over` it, a year
 * say, is set on clear ground: no tile nearer any of its boxes than the gap between
 * tiles, those that would be cut back short of it. One group,
 * the grid and what is over it. */
export const DrawBackpackGrid = ({
  bleed,
  box,
  ground = PALETTE.paper,
  inks = [PALETTE.primary, PALETTE.secondary, PALETTE.text, PALETTE.lines],
  over = [],
  random,
  short,
}: {
  bleed: Margins<Distance>
  box: SpatialRectangle<Distance>
  ground?: ColorID
  inks?: ColorID[]
  over?: PageShape[]
  random: RandomStream
  short: Distance
}): PageShape[] => {
  const area = AddMargins(box, bleed)
  // The cells as near square as a whole number of them fills the box; none
  // in a box under half a cell, a spine's, its ground alone
  const columns = Round(box.width / Scale(short, CELL))
  const rows = Round(box.height / Scale(short, CELL))
  const cell_x = Scale(box.width, 1 / columns)
  const cell_y = Scale(box.height, 1 / rows)
  const inset = Scale(Min(cell_x, cell_y), GAP / 2)
  const taken = Array.from({ length: rows }, () => Array.from({ length: columns }, Constant(false)))
  // Each shape set over the grid, as far from the tiles as they are from
  // each other: a tile reaching into its room cut back to the side with the
  // most of it left, and left out when what is left is under SMALLEST of a
  // cell
  const kept = over.map(shape =>
    AddMargins(ShapeBounds(shape as PlotShape), ToMargins(Scale(inset, 2)))
  )
  const smallest = Scale(Min(cell_x, cell_y), SMALLEST)
  const clear = (tile: SpatialRectangle<Distance> | undefined) =>
    kept.reduce((left, room) => left && clear_of(left, room), tile)
  const clear_of = (
    tile: SpatialRectangle<Distance>,
    kept: SpatialRectangle<Distance>
  ): SpatialRectangle<Distance> | undefined => {
    if (
      tile.x >= Total(kept.x, kept.width) ||
      Total(tile.x, tile.width) <= kept.x ||
      tile.y >= Total(kept.y, kept.height) ||
      Total(tile.y, tile.height) <= kept.y
    )
      return tile
    const right = Total(tile.x, tile.width)
    const top = Total(tile.y, tile.height)
    const sides = [
      { ...tile, width: Subtract(kept.x, tile.x) },
      { ...tile, x: Total(kept.x, kept.width), width: Subtract(right, Total(kept.x, kept.width)) },
      { ...tile, height: Subtract(kept.y, tile.y) },
      { ...tile, y: Total(kept.y, kept.height), height: Subtract(top, Total(kept.y, kept.height)) },
    ].filter(side => side.width >= smallest && side.height >= smallest)
    return sides.sort((a, b) => b.width * b.height - a.width * a.height)[0]
  }
  const free = (row: number, column: number) =>
    row < rows && column < columns && !taken[row]![column]
  const shapes: PageShape[] = []
  for (let row = 0; row < rows; row++)
    for (let column = 0; column < columns; column++) {
      if (!free(row, column)) continue
      const [across, up] = RandomPick(random, SIZES)
      // As wide as the row has room, then as tall as every column of it has
      let width = 1
      while (width < across && free(row, column + width)) width++
      let height = 1
      while (height < up && [...Range(width)].every(i => free(row + height, column + i))) height++
      for (let r = row; r < row + height; r++) taken[r]!.fill(true, column, column + width)
      const fill_color = RandomPick(random, inks)
      const tile = clear({
        height: Subtract(Scale(cell_y, height), Scale(inset, 2)),
        width: Subtract(Scale(cell_x, width), Scale(inset, 2)),
        x: Total(box.x, Scale(cell_x, column), inset),
        y: Total(box.y, Scale(cell_y, row), inset),
      })
      if (!tile) continue
      const radius = Min(
        Scale(Min(cell_x, cell_y), RADIUS),
        Scale(Min(tile.width, tile.height), 0.5)
      )
      const points = RoundedRect(tile, radius)
      // the gray ones the pantone too, on its own plate
      for (const color of fill_color === PALETTE.lines ? [fill_color, PALETTE.spot] : [fill_color])
        shapes.push({ closed: true, enum: PlotFunction.PLOT_PATH, fill_color: color, points })
    }
  return [
    {
      enum: PlotFunction.PLOT_GROUP,
      matrix: MatrixIdentity<Distance>(),
      shapes: [
        ...ClipShapes({
          box: area,
          shapes: [{ enum: PlotFunction.PLOT_RECT, fill_color: ground, position: area }, ...shapes],
        }),
        ...over,
      ],
    } as PageShape,
  ]
}
